import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  Music,
  Calendar,
  Trash2,
  FileText,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { AdminAuth, AdminShell, adminHeaders } from "@/components/admin/AdminShell";
import ContractDialog from "@/components/admin/ContractDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminQuotes() {
  return (
    <AdminAuth>
      <Inner />
    </AdminAuth>
  );
}

function Inner() {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [contractPrefill, setContractPrefill] = useState(null);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quotes`, { headers: adminHeaders() });
      setQuotes(res.data);
    } catch {
      toast.error("Failed to load quotes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const onDelete = async (id) => {
    if (!confirm("Delete this quote request? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/quotes/${id}`, { headers: adminHeaders() });
      setQuotes((q) => q.filter((x) => x.id !== id));
      toast.success("Quote deleted.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  const projectTypes = Array.from(new Set(quotes.map((q) => q.project_type)));
  const filtered =
    filter === "all" ? quotes : quotes.filter((q) => q.project_type === filter);

  // Map a quote's project_type into the contract's selected_services list
  const SERVICE_MAP = {
    "Live Recording": ["Live Recordings"],
    "Studio Recording": ["Studio Recordings"],
    "Production Arranging": ["Production"],
    "Mixing": ["Production"],
    "Mastering": ["Production"],
    "Audio Engineering": ["Production"],
  };

  const onGenerateContract = (q) => {
    setContractPrefill({
      quote_id: q.id,
      client_name: q.name,
      client_email: q.email,
      client_phone: q.phone || "",
      event_dates: q.event_date || "",
      selected_services: SERVICE_MAP[q.project_type] || [],
      additional_services: q.message || "",
    });
    setContractOpen(true);
  };

  return (
    <AdminShell onRefresh={fetchQuotes} refreshing={loading}>
      <div data-testid="admin-quotes-page">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard label="Total Requests" value={quotes.length} />
          <StatCard
            label="Live Recordings"
            value={quotes.filter((q) => q.project_type === "Live Recording").length}
            accent="text-brand-orange"
          />
          <StatCard
            label="Mixing / Mastering"
            value={
              quotes.filter((q) => ["Mixing", "Mastering"].includes(q.project_type)).length
            }
            accent="text-brand-magenta"
          />
          <StatCard
            label="Studio / Production"
            value={
              quotes.filter((q) =>
                ["Studio Recording", "Production Arranging"].includes(q.project_type)
              ).length
            }
            accent="text-brand-cyan"
          />
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          <FilterChip
            active={filter === "all"}
            onClick={() => setFilter("all")}
            label={`All (${quotes.length})`}
          />
          {projectTypes.map((t) => (
            <FilterChip
              key={t}
              active={filter === t}
              onClick={() => setFilter(t)}
              label={`${t} (${quotes.filter((q) => q.project_type === t).length})`}
            />
          ))}
        </div>

        {filtered.length === 0 ? (
          <div
            data-testid="admin-empty-state"
            className="rounded-3xl border border-white/10 bg-white/[0.02] p-16 text-center"
          >
            <Mail size={32} className="mx-auto text-white/30" />
            <p className="mt-4 font-display font-bold text-xl text-white">
              No quote requests yet.
            </p>
            <p className="mt-2 font-body text-white/50 text-sm">
              When artists submit the form, their requests will appear here.
            </p>
          </div>
        ) : (
          <ul className="space-y-4" data-testid="admin-quotes-list">
            {filtered.map((q) => (
              <li
                key={q.id}
                data-testid={`admin-quote-${q.id}`}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.04] transition-colors"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display font-extrabold text-xl text-white">
                        {q.name}
                      </h3>
                      <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] bg-brand-magenta/20 text-brand-magenta border border-brand-magenta/30">
                        {q.project_type}
                      </span>
                      <span className="font-body text-xs text-white/40">
                        {new Date(q.created_at).toLocaleString()}
                      </span>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/70 font-body">
                      <a
                        href={`mailto:${q.email}`}
                        className="inline-flex items-center gap-2 hover:text-white"
                      >
                        <Mail size={14} /> {q.email}
                      </a>
                      {q.phone && (
                        <a
                          href={`tel:${q.phone}`}
                          className="inline-flex items-center gap-2 hover:text-white"
                        >
                          <Phone size={14} /> {q.phone}
                        </a>
                      )}
                      <span className="inline-flex items-center gap-2">
                        <Music size={14} /> {q.number_of_songs}{" "}
                        {q.number_of_songs === 1 ? "song" : "songs"}
                      </span>
                      {q.event_date && (
                        <span className="inline-flex items-center gap-2 text-brand-orange">
                          <Calendar size={14} /> {q.event_date}
                        </span>
                      )}
                    </div>

                    {q.message && (
                      <p className="mt-4 font-body text-sm text-white/65 leading-relaxed whitespace-pre-wrap">
                        {q.message}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      type="button"
                      onClick={() => onGenerateContract(q)}
                      data-testid={`admin-generate-contract-${q.id}`}
                      title="Generate contract from this quote"
                      className="rounded-full h-9 px-4 bg-white/5 text-white hover:bg-brand-magenta hover:text-white border border-white/10 font-display font-bold text-xs"
                    >
                      <FileText size={14} className="mr-1.5" />
                      Generate Contract
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => onDelete(q.id)}
                      data-testid={`admin-delete-${q.id}`}
                      aria-label="Delete quote"
                      className="text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-full h-9 w-9 p-0"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <ContractDialog
        open={contractOpen}
        onOpenChange={setContractOpen}
        prefill={contractPrefill}
      />
    </AdminShell>
  );
}

function StatCard({ label, value, accent = "text-white" }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
      <p className="font-body text-[11px] uppercase tracking-[0.22em] text-white/50">
        {label}
      </p>
      <p className={`mt-3 font-display font-black text-4xl tracking-tight ${accent}`}>
        {value}
      </p>
    </div>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-full font-body text-xs uppercase tracking-[0.18em] border transition-colors ${
        active
          ? "bg-white text-black border-white"
          : "bg-white/[0.02] text-white/65 border-white/10 hover:text-white hover:border-white/30"
      }`}
    >
      {label}
    </button>
  );
}
